import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Checkbox, FormControlLabel, Stack } from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { ROOM_RATE_FLASH_TYPE } from '~/consts/ratePlans';
import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';
import { ROLE_ADMIN_USER, ROLE_COORDINATOR } from '~/consts/roles';

import { usePatchRoomRateInterconnecting } from '~/hooks/apis/reservation/usePatchRoomRateInterconnecting';

interface Props {
  propertyId: string;
  roomTypeId: string;
  roomRate: App.RoomRate;
  channelManager?: string | null;
}

const ENABLE_CONFIRM_TITLE = 'Enable interconnecting room rate?';
const ENABLE_CONFIRM_DESCRIPTION =
  'This rate will require 2 physical rooms at the hotel. Availability, guest allocation, and pricing calculations will change.';
const DISABLE_CONFIRM_TITLE = 'Disable interconnecting room rate?';
const DISABLE_CONFIRM_DESCRIPTION =
  'This rate will no longer be treated as an interconnecting room. Availability, guest allocation, and pricing calculations will revert to standard behaviour.';

export default function RoomRateInterconnectingFlag({ propertyId, roomTypeId, roomRate, channelManager }: Props) {
  const showConfirmDialog = useConfirmDialog();
  const { mutateAsync, isPending, error, reset } = usePatchRoomRateInterconnecting();
  const [isInterconnecting, setIsInterconnecting] = useState(roomRate.is_flash_interconnecting_room ?? false);

  useEffect(() => {
    setIsInterconnecting(roomRate.is_flash_interconnecting_room ?? false);
    reset();
  }, [roomRate.id, roomRate.is_flash_interconnecting_room, reset]);

  const isEligible =
    channelManager === PROPERTY_CHANNEL_MANAGERS.derbysoft && roomRate.rate_plan?.product_type === ROOM_RATE_FLASH_TYPE;

  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.checked;
      const previousValue = isInterconnecting;

      if (nextValue === previousValue) {
        return;
      }

      const confirmed = await showConfirmDialog({
        title: nextValue ? ENABLE_CONFIRM_TITLE : DISABLE_CONFIRM_TITLE,
        description: nextValue ? ENABLE_CONFIRM_DESCRIPTION : DISABLE_CONFIRM_DESCRIPTION,
      });

      if (!confirmed) {
        return;
      }

      try {
        await mutateAsync({
          propertyId,
          roomTypeId,
          roomRateId: roomRate.id,
          isInterconnecting: nextValue,
        });
        setIsInterconnecting(nextValue);
      } catch {
        // Error state is surfaced via the mutation's `error`.
      }
    },
    [isInterconnecting, mutateAsync, propertyId, roomRate.id, roomTypeId, showConfirmDialog],
  );

  if (!isEligible) {
    return null;
  }

  return (
    <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_COORDINATOR]}>
      <Stack spacing={1} mb={2}>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <FormControlLabel
            label="Interconnecting room (requires 2 physical rooms at hotel)"
            control={
              <Checkbox
                id="room-rate-interconnecting-toggle"
                name="room-rate-interconnecting-toggle"
                checked={isInterconnecting}
                disabled={isPending}
                onChange={handleChange}
                size="small"
              />
            }
          />
        </Stack>
        {error && <Alert severity="error">{error.message ?? 'Failed to update interconnecting room flag'}</Alert>}
      </Stack>
    </PermissionedComponent>
  );
}
